import noop from 'lodash/noop';
import first from 'lodash/first';

import { RxSocketUrls } from '@Constants/CommonConstants';
import { generateResultsPath } from '@AppRoutes/utils';
import history from '@AppRoutes/history';
import SocketBroker from '@Services/SocketBroker';
import UserDatabases from '@Services/UserDatabases';
import Breadcrumb from '@Services/Breadcrumb';
import searchStorage from '@Services/SearchStorage';
import ResultsInfo from '@Services/ResultsInfo';
import PriorityWatcher from '../PriorityWatcher';

export default class ResultsRunner {
  constructor({ searchId, requestId, queryFactItem, context, databases }, { onSuccess, onEmptyResults, onError } = {}) {
    this.config = {
      searchId,
      requestId,
      queryFactItem,
      context,
      databases,
    };

    this.callbacks = {
      onSuccess,
      onEmptyResults,
      onError,
    };

    this._isResultsShown = false;
    this._unsubscribeSockets = noop;
    this._priorityWatcher = new PriorityWatcher({ databaseId: first(UserDatabases.getAll()).id });
  }

  __initResultsInfo = (payload) => {
    const { items } = payload;

    this._priorityWatcher.init(items, 'queryId');
    ResultsInfo.startLoading(items);
  };

  __updateResultsInfo = (payload) => {
    const { queryId } = payload;

    this._priorityWatcher.addItem(payload, 'queryId');
    ResultsInfo.updateInfo({ ...payload, pageNumber: 1, description: null }, queryId);
  };

  ___showResults = async (result, groupId) => {
    const { queryFactItem } = this.config;
    const { onSuccess } = this.callbacks;
    const { searchId, resultName } = result;

    this._isResultsShown = true;

    Breadcrumb.resetBreadcrumbs();

    await searchStorage.extendItem(searchId, { queryFactItem });
    await ResultsInfo.commitResultsToBreadcrumbs(groupId);

    if (onSuccess) {
      onSuccess();
    }
    if (resultName) {
      const pathName = generateResultsPath(result);
      history.push({
        pathname: pathName,
        state: result,
      });
    }
  };

  __checkEmptyResults = (result) => {
    const { onEmptyResults } = this.callbacks;
    const { resultsCount } = result;

    if (resultsCount === 0 && this._priorityWatcher.isAllZeroCount()) {
      this._unsubscribeSockets('empty results');
      if (onEmptyResults) {
        onEmptyResults();
      }
    }
  };

  __checkDelayedData = () => {
    if (this._isResultsShown) {
      ResultsInfo.delayedResultsInfoSubject.next();
    }
  };

  __checkHighPriorityResult = () => {
    // Try to get completed result with highest priority
    const highPriorityResult = this._priorityWatcher.getHighPriorityItemResults();

    if (!this._isResultsShown && highPriorityResult) {
      const highPriorityItem = this._priorityWatcher.getHighPriorityItem();

      this.___showResults(highPriorityResult, highPriorityItem.groupId);
    }
  };

  __checkAllResultsLoaded = () => {
    // unsubscribe only all data has come
    if (this._isResultsShown && this._priorityWatcher.isAllResultsLoaded()) {
      this._unsubscribeSockets('cleanup search finished');
    }
  };

  _handleSearchResults = (result) => {
    const { searchId } = this.config;
    let { payload } = result;
    if (!payload) {
      payload = result.data.payload;
    }

    if (searchId !== payload.searchId) {
      return;
    }

    const { resultName, resultSize: resultsCount } = payload;

    if (!resultName) {
      this.__initResultsInfo(payload);
    } else {
      this.__updateResultsInfo({ ...payload, resultsCount });
    }

    // sequence is important
    this.__checkEmptyResults({ ...payload, resultsCount });
    this.__checkDelayedData();
    this.__checkHighPriorityResult();
    this.__checkAllResultsLoaded();

    // eslint-disable-next-line
    return result;
  };

  run() {
    const { searchId, requestId, queryFactItem, context, databases } = this.config;

    this._isResultsShown = false;
    this._priorityWatcher.clear();

    const request = {
      queryFactItem,
      context,
      databases,
      searchId,
      requestId,
    };

    Logger.info('WS AlertResults request:', request);

    const unsubscribeErrors = SocketBroker.observe(RxSocketUrls.ChannelsUrls.QueueErrors, (data) => {
      if (this._unsubscribeSockets) {
        this._unsubscribeSockets('error in search');
      }
      if (this.callbacks?.onError) {
        this.callbacks?.onError(data);
      }
    });
    const socketSubject = SocketBroker.send(
      {
        send: RxSocketUrls.AdvancedQuery,
        cancel: RxSocketUrls.CancelSearch,
        message: RxSocketUrls.ChannelsUrls.SearchInfo,
      },
      {
        searchId,
        params: request,
      }
    );

    socketSubject.subscribe(this._handleSearchResults, Logger.error);

    this._unsubscribeSockets = (reason) => {
      socketSubject.cancel(reason);
      unsubscribeErrors();
    };

    return socketSubject;
  }
}
